/**
 * Customização AgSUS de @plone/volto/components/manage/Blocks/Video/Body.
 *
 * Diferenças em relação ao core (18.33.1):
 * 1. Aceita o endereço de Shorts do YouTube (youtube.com/shorts/<id>), que o
 *    core não reconhece: o identificador ficava vazio e o player não carregava.
 * 2. Shorts são verticais: o bloco sai em 9:16, estreito e centralizado
 *    (classe `is-vertical`, regras em video.css). O Embed do Semantic UI só
 *    aceita 4:3, 16:9 e 21:9, por isso a proporção vem do CSS.
 * 3. Um clique só: o vídeo toca ao clicar na miniatura. O Embed monta a URL
 *    do player com `&amp;` literal entre os parâmetros (o YouTube e o Vimeo
 *    só leem o primeiro, então o autoplay nunca chegava) e sem permissão de
 *    autoplay nem de tela cheia no iframe; o clique só trocava a miniatura
 *    pelo player parado. Aqui a URL e as permissões vão prontas no `iframe`.
 * 4. Aceita também o endereço de incorporação (youtube.com/embed/<id>), que
 *    veio no conteúdo migrado do WordPress: o core não o reconhece e o vídeo
 *    saía sem miniatura e com o player em embed/null.
 * 5. A miniatura ganha alt vazio: o Embed desenha a <img class="placeholder">
 *    sem texto alternativo, e o leitor de tela anunciava o endereço da imagem.
 *    Ela é decorativa (repete o vídeo), por isso alt="".
 */
import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Embed, Message } from 'semantic-ui-react';
import cx from 'classnames';
import { isInternalURL, flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import './video.css';

//Extracting videoID, listID and thumbnailURL from the video URL
const getVideoIDAndPlaceholder = (url) => {
  let videoID = null;
  let listID = null;
  let thumbnailURL = null;
  let isVertical = false;

  if (url) {
    if (url.match('youtu')) {
      if (url.match('list')) {
        const matches = url.match(/^.*\?list=(.*)|^.*&list=(.*)$/);
        listID = matches[1] || matches[2];

        let thumbnailID = null;
        if (url.match(/\?v=(.*)&list/)) {
          thumbnailID = url.match(/^.*\?v=(.*)&list(.*)/)[1];
        }
        if (url.match(/\?v=(.*)\?list/)) {
          thumbnailID = url.match(/^.*\?v=(.*)\?list(.*)/)[1];
        }
        thumbnailURL =
          'https://img.youtube.com/vi/' + thumbnailID + '/sddefault.jpg';
      } else if (url.match(/\/shorts\//)) {
        videoID = url.match(/\/shorts\/([^?&#/]+)/)[1];
        isVertical = true;
      } else if (url.match(/\/embed\//)) {
        videoID = url.match(/\/embed\/([^?&#/]+)/)[1];
      } else if (url.match('live')) {
        videoID = url.match(/^.*\/live\/(.*)/)[1];
      } else if (url.match(/\.be\//)) {
        videoID = url.match(/^.*\.be\/(.*)/)[1];
      } else if (url.match(/\?v=/)) {
        videoID = url.match(/^.*\?v=(.*)$/)[1];
      }

      if (videoID) {
        let thumbnailID = videoID;
        if (videoID.match(/\?si=/)) {
          thumbnailID = videoID.match(/(.*)\?si=(.*)/)[1];
        }
        //load video preview image from youtube
        thumbnailURL =
          'https://img.youtube.com/vi/' + thumbnailID + '/sddefault.jpg';
      }
    } else if (url.match('vimeo')) {
      videoID = url.match(/^.*\.com\/(.*)/)[1];
      if (videoID) {
        let thumbnailID = videoID;
        if (videoID.match(/\?si=/)) {
          thumbnailID = videoID.match(/(.*)\?si=(.*)/)[1];
        }
        thumbnailURL = 'https://vumbnail.com/' + thumbnailID + '.jpg';
      }
    }
  }
  return { videoID, listID, thumbnailURL, isVertical };
};

// Só o identificador: descarta o que vier depois dele (?si=, &t=, #...).
const semParametros = (id) => (id ? id.split(/[?&#]/)[0] : id);

const iframeDoPlayer = (src, fonte) => ({
  src,
  allow: 'autoplay; encrypted-media; picture-in-picture; fullscreen',
  allowFullScreen: true,
  title: `Vídeo do ${fonte}`,
});

const Body = ({ data, isEditMode }) => {
  let placeholder = data.preview_image
    ? isInternalURL(data.preview_image)
      ? `${flattenToAppURL(data.preview_image)}/@@images/image`
      : data.preview_image
    : null;

  const { videoID, listID, thumbnailURL, isVertical } =
    getVideoIDAndPlaceholder(data.url);

  placeholder = !placeholder ? thumbnailURL : placeholder;

  const ref = React.createRef();
  const caixa = React.useRef(null);
  React.useEffect(() => {
    caixa.current
      ?.querySelectorAll('img.placeholder:not([alt])')
      .forEach((img) => img.setAttribute('alt', ''));
  });
  const onKeyDown = (e) => {
    if (e.nativeEvent.keyCode === 13) {
      ref.current.handleClick();
    }
  };

  const embedSettings = {
    placeholder: placeholder,
    icon: 'play',
    defaultActive: false,
    autoplay: false,
    ...(isVertical ? {} : { aspectRatio: '16:9' }),
    tabIndex: 0,
    onKeyPress: onKeyDown,
    ref: ref,
  };

  return (
    <>
      {data.url && (
        <div
          ref={caixa}
          className={cx('video-inner', {
            'full-width': data.align === 'full',
            'is-vertical': isVertical,
          })}
        >
          {data.url.match('youtu') ? (
            <>
              {data.url.match('list') ? (
                <Embed
                  source="youtube"
                  iframe={iframeDoPlayer(
                    `https://www.youtube.com/embed/videoseries?list=${semParametros(listID)}&autoplay=1&playsinline=1`,
                    'YouTube',
                  )}
                  {...embedSettings}
                />
              ) : (
                <Embed
                  id={videoID}
                  source="youtube"
                  iframe={iframeDoPlayer(
                    `https://www.youtube.com/embed/${semParametros(videoID)}?autoplay=1&rel=0&playsinline=1`,
                    'YouTube',
                  )}
                  {...embedSettings}
                />
              )}
            </>
          ) : (
            <>
              {data.url.match('vimeo') ? (
                <Embed
                  id={videoID}
                  source="vimeo"
                  iframe={iframeDoPlayer(
                    `https://player.vimeo.com/video/${semParametros(videoID)}?autoplay=1&byline=0&portrait=0&title=0`,
                    'Vimeo',
                  )}
                  {...embedSettings}
                />
              ) : (
                <>
                  {data.url.match('.mp4') ? (
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    <video
                      src={
                        isInternalURL(data.url)
                          ? data.url.includes('@@download')
                            ? data.url
                            : `${flattenToAppURL(data.url)}/@@download/file`
                          : data.url
                      }
                      controls
                      poster={placeholder}
                      type="video/mp4"
                    />
                  ) : isEditMode ? (
                    <div>
                      <Message>
                        <center>
                          <FormattedMessage
                            id="Please enter a valid URL by deleting the block and adding a new video block."
                            defaultMessage="Please enter a valid URL by deleting the block and adding a new video block."
                          />
                        </center>
                      </Message>
                    </div>
                  ) : (
                    <div className="invalidVideoFormat" />
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
Body.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default Body;
export { getVideoIDAndPlaceholder };
